import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Salad, ShoppingCart, Loader2, Sparkles, Check, Truck } from "lucide-react";
import { toast } from "sonner";

export default function Nutrition() {
  const [form, setForm] = useState({ goal: "muscle gain", calories: 2200, diet: "balanced", days: 3 });
  const [plan, setPlan] = useState(null);
  const [genPlan, setGenPlan] = useState(false);
  const [genGroc, setGenGroc] = useState(false);
  const [lists, setLists] = useState([]);

  const loadLists = useCallback(async () => {
    const res = await api.get("/grocery");
    setLists(res.data);
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);

  const generatePlan = async () => {
    setGenPlan(true); setPlan(null);
    try {
      const res = await api.post("/ai/meal-plan", { ...form, calories: Number(form.calories), days: Number(form.days) });
      setPlan(res.data);
      toast.success("Meal plan generated!");
    } catch { toast.error("Could not generate plan"); } finally { setGenPlan(false); }
  };

  const generateGrocery = async () => {
    if (!plan) return;
    setGenGroc(true);
    try {
      await api.post("/ai/grocery", plan);
      toast.success("Grocery list created!");
      loadLists();
    } catch { toast.error("Could not build grocery list"); } finally { setGenGroc(false); }
  };

  const toggleItem = async (list, item) => {
    setLists((ls) => ls.map((l) => l.list_id === list.list_id
      ? { ...l, purchased: l.purchased?.includes(item) ? l.purchased.filter((x) => x !== item) : [...(l.purchased || []), item] }
      : l));
    await api.post(`/grocery/${list.list_id}/toggle`, { item });
  };

  return (
    <div>
      <h1 className="font-display font-bold text-4xl uppercase tracking-tight text-white mb-1">Nutrition Lab</h1>
      <p className="text-sm text-[#A0A0A0] mb-6">AI macro meal plans that auto-generate your grocery run.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Generator */}
        <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-5 h-fit">
          <div className="flex items-center gap-2 mb-4"><Salad className="w-5 h-5 text-[#FF6B6B]" /><h3 className="font-bold text-white">Meal Plan Generator</h3></div>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Goal</label>
              <select data-testid="goal-select" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]">
                <option>muscle gain</option><option>fat loss</option><option>maintenance</option><option>endurance</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Daily Calories</label>
              <input data-testid="calories-input" type="number" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Diet</label>
              <select data-testid="diet-select" value={form.diet} onChange={(e) => setForm({ ...form, diet: e.target.value })} className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]">
                <option>balanced</option><option>high-protein</option><option>vegan</option><option>keto</option><option>vegetarian</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#A0A0A0]">Days: {form.days}</label>
              <input data-testid="days-range" type="range" min="1" max="7" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="w-full mt-2 accent-[#FF6B6B]" />
            </div>
            <button data-testid="generate-plan-btn" onClick={generatePlan} disabled={genPlan} className="w-full py-3 rounded-xl bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-50">
              {genPlan ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate Plan</>}
            </button>
          </div>
        </div>

        {/* Plan result */}
        <div className="lg:col-span-2">
          {!plan && !genPlan && <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-10 text-center text-[#A0A0A0]">Configure and generate your AI meal plan to see it here.</div>}
          {genPlan && <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-10 flex justify-center"><Loader2 className="w-8 h-8 text-[#FF6B6B] animate-spin" /></div>}
          {plan && (
            <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-5" data-testid="meal-plan-result">
              <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className="font-display font-bold text-2xl uppercase tracking-tight text-white">{plan.title}</h3>
                  <div className="flex gap-3 mt-1 text-xs text-[#A0A0A0]">
                    <span className="text-[#FF6B6B] font-semibold">{plan.daily_calories} kcal</span>
                    {plan.macros && <><span>P {plan.macros.protein_g}g</span><span>C {plan.macros.carbs_g}g</span><span>F {plan.macros.fat_g}g</span></>}
                  </div>
                </div>
                <button data-testid="generate-grocery-btn" onClick={generateGrocery} disabled={genGroc} className="px-4 py-2 rounded-full bg-[#4ECCD3] text-black text-sm font-bold flex items-center gap-2 hover:brightness-110 transition-all disabled:opacity-50">
                  {genGroc ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} Build Grocery List
                </button>
              </div>
              <div className="space-y-4 max-h-[420px] overflow-y-auto no-scrollbar">
                {(plan.days || []).map((d) => (
                  <div key={d.day}>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B6B] mb-2">Day {d.day}</p>
                    <div className="space-y-2">
                      {(d.meals || []).map((m, i) => (
                        <div key={i} className="bg-[#121212] rounded-xl p-3">
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-sm font-semibold text-white">{m.name}</p>
                            <span className="text-xs text-[#A0A0A0]">{m.calories} kcal · {m.protein_g}g P</span>
                          </div>
                          <p className="text-xs text-[#A0A0A0]">{(m.items || []).join(" · ")}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grocery lists */}
      {lists.length > 0 && (
        <div className="mt-8">
          <h2 className="font-bold text-white mb-3 flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-[#4ECCD3]" /> Your Grocery Lists</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {lists.map((list) => (
              <div key={list.list_id} className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-5" data-testid={`grocery-list-${list.list_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-white">{list.title}</h3>
                  <button data-testid={`export-delivery-${list.list_id}`} onClick={() => toast.success("Exported to Instacart cart (simulated) 🛒")} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B] hover:bg-[#FF6B6B]/20 transition-colors">
                    <Truck className="w-3.5 h-3.5" /> Export
                  </button>
                </div>
                <div className="space-y-3 max-h-72 overflow-y-auto no-scrollbar">
                  {(list.categories || []).map((cat, ci) => (
                    <div key={ci}>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0] mb-1.5">{cat.aisle}</p>
                      <div className="space-y-1">
                        {(cat.items || []).map((it, ii) => {
                          const label = `${it.name}${it.quantity ? ` — ${it.quantity}` : ""}`;
                          const done = list.purchased?.includes(label);
                          return (
                            <button key={ii} data-testid="grocery-item" onClick={() => toggleItem(list, label)} className="w-full flex items-center gap-2 py-1.5 text-left group">
                              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${done ? "bg-[#FF6B6B] border-[#FF6B6B]" : "border-[#A0A0A0] group-hover:border-white"}`}>
                                {done && <Check className="w-3 h-3 text-black" />}
                              </div>
                              <span className={`text-sm ${done ? "text-[#A0A0A0] line-through" : "text-white"}`}>{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
